"use client";

import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

/**
 * Minimal-header nav link — a quiet text link with an animated accent
 * underline that grows on hover and stays lit on the active page.
 *
 * Active match: exact `/` for home, else prefix match so section pages
 * (`/about/history`) keep the parent (`/about`) lit. Client-only because
 * it reads the current pathname.
 */
function isActive(pathname: string, href: string): boolean {
  if (!href || href === "#") return false;
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(href + "/");
}

export function MinimalNavLink({
  href,
  target,
  rel,
  children,
}: {
  href: string;
  target?: string;
  rel?: string;
  children: React.ReactNode;
}) {
  const pathname = usePathname() || "/";
  const active = isActive(pathname, href);

  return (
    <Link
      href={href}
      target={target}
      rel={rel}
      aria-current={active ? "page" : undefined}
      className={`group/link relative inline-flex items-center gap-1 whitespace-nowrap px-3 py-2 text-sm font-medium tracking-tight transition-colors ${
        active
          ? "text-[color:var(--color-primary)]"
          : "text-foreground/80 hover:text-[color:var(--color-primary)]"
      }`}
    >
      {children}
      <span
        aria-hidden
        className={`pointer-events-none absolute inset-x-3 bottom-1 h-[2px] origin-left rounded-full transition-transform duration-200 ease-out ${
          active ? "scale-x-100" : "scale-x-0 group-hover/link:scale-x-100"
        }`}
        style={{ backgroundColor: "var(--color-accent)" }}
      />
    </Link>
  );
}

export default MinimalNavLink;
